/**
 * Token storage utilities.
 *
 * Refresh tokens are persisted to localStorage so users stay logged in
 * across browser restarts for the duration of the server-side token expiry.
 *
 * Access tokens are NEVER persisted — they live in Zustand memory only
 * and are refreshed automatically by the HTTP interceptor on 401.
 *
 * This module has zero external dependencies so it can be safely imported
 * by both the HTTP interceptor layer (src/api/) and the auth feature layer
 * (src/features/auth/) without creating circular module graphs.
 */

const RT_KEY = 'gw_erp_rt'; // localStorage — refresh token
const AT_KEY = 'gw_erp_at'; // localStorage — access token persists across tabs
const PERMS_KEY = 'gw_erp_perms'; // localStorage — permission codes persists across tabs

export const tokenStorage = {
  // ── Refresh token (localStorage) ─────────────────────────────────────────
  getRefreshToken: (): string | null => localStorage.getItem(RT_KEY),
  setRefreshToken: (token: string): void => localStorage.setItem(RT_KEY, token),
  removeRefreshToken: (): void => localStorage.removeItem(RT_KEY),

  // ── Access token (localStorage) ─────────────────────────────────────
  getAccessToken: (): string | null => localStorage.getItem(AT_KEY),
  setAccessToken: (token: string): void => localStorage.setItem(AT_KEY, token),
  removeAccessToken: (): void => localStorage.removeItem(AT_KEY),

  // ── Permissions (localStorage) ─────────────────────────────────────
  getPermissions: (): string[] | null => {
    const raw = localStorage.getItem(PERMS_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as string[];
    } catch {
      return null;
    }
  },
  setPermissions: (perms: string[]): void => localStorage.setItem(PERMS_KEY, JSON.stringify(perms)),
  removePermissions: (): void => localStorage.removeItem(PERMS_KEY),
} as const;
